import React, { Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import ErrorBoundary from "../Pages/ErrorBoundary";
import AuthGuard from "../Routes/AuthGuard";
import { useDeviceType, DeviceType } from "./useDeviceType";

/**
 * ResponsiveRouters mirrors the route table in Routes/AppRouters.tsx but resolves
 * each route to the Mobile / Tablet / Desktop variant of the page based on the
 * current viewport. The desktop branch reuses the existing components in /Components,
 * the mobile branch loads from /Mobile/Pages, and the tablet branch loads from
 * /Tablet/Pages — none of the legacy code is touched.
 */

/* ========================================================================
 * Eagerly-loaded auth / shell pages (kept eager to mirror AppRouters.tsx)
 * ====================================================================== */
import CapitalMarketsStaticDesktop from "../Components/HomepageStatic/CapitalMarketsStatic";
import LoginDesktop from "../Components/Main/HomePage/Authentication/Login";
import SignUpDesktop from "../Components/Main/HomePage/Authentication/SignUp";
import ErrorPageDesktop from "../Pages/ErrorPage";
import EmailVerificationDesktop from "../Components/Main/HomePage/Authentication/EmailVerification";
import ResetPasswordDesktop from "../Components/Main/HomePage/Authentication/ResetPassword";
import SummaryPopupDesktop from "../Components/Main/HomePage/Authentication/SummaryPopup";
import PageUnderDevelopmentDesktop from "../Pages/PageUnderDevelopment";
import MDTechnicalAnalysisDesktop from "../Components/Main/MonasheeDeals/MDTechnicalAnalysis";
import ShowQuantAnalysisDesktop from "../Components/AIML/ShowQuantAnalysis";
import ShowQuantAnalysisDetailsDesktop from "../Components/AIML/ShowQuantAnalysisDetails";
import QuantAnalysisUploadDesktop from "../Components/AIML/QuantAnalysisUpload";
import QuantIPODashboardPage from "../Components/AIML/QuantIPODashboardPage";
import QuantAnalysisDetailsModal from "../Components/AIML/QuantAnalysisDetailsModal";

/* Eager mobile counterparts */
import CapitalMarketsStaticMobile from "../Mobile/Pages/CapitalMarketsStatic/CapitalMarketsStatic";
import LoginMobile from "../Mobile/Pages/Login/Login";
import SignUpMobile from "../Mobile/Pages/SignUp/SignUp";
import ErrorPageMobile from "../Mobile/Pages/ErrorPage/ErrorPage";
import EmailVerificationMobile from "../Mobile/Pages/EmailVerification/EmailVerification";
import ResetPasswordMobile from "../Mobile/Pages/ResetPassword/ResetPassword";
import SummaryPopupMobile from "../Mobile/Pages/SummaryPopup/SummaryPopup";
import PageUnderDevelopmentMobile from "../Mobile/Pages/PageUnderDevelopment/PageUnderDevelopment";
import MDTechnicalAnalysisMobile from "../Mobile/Pages/MDTechnicalAnalysis/MDTechnicalAnalysis";
import ShowQuantAnalysisMobile from "../Mobile/Pages/ShowQuantAnalysis/ShowQuantAnalysis";
import ShowQuantAnalysisDetailsMobile from "../Mobile/Pages/ShowQuantAnalysisDetails/ShowQuantAnalysisDetails";
import QuantAnalysisUploadMobile from "../Mobile/Pages/QuantAnalysisUpload/QuantAnalysisUpload";

/* Eager tablet counterparts */
import CapitalMarketsStaticTablet from "../Tablet/Pages/CapitalMarketsStatic/CapitalMarketsStatic";
import LoginTablet from "../Tablet/Pages/Login/Login";
import SignUpTablet from "../Tablet/Pages/SignUp/SignUp";
import ErrorPageTablet from "../Tablet/Pages/ErrorPage/ErrorPage";
import EmailVerificationTablet from "../Tablet/Pages/EmailVerification/EmailVerification";
import ResetPasswordTablet from "../Tablet/Pages/ResetPassword/ResetPassword";
import SummaryPopupTablet from "../Tablet/Pages/SummaryPopup/SummaryPopup";
import PageUnderDevelopmentTablet from "../Tablet/Pages/PageUnderDevelopment/PageUnderDevelopment";
import MDTechnicalAnalysisTablet from "../Tablet/Pages/MDTechnicalAnalysis/MDTechnicalAnalysis";
import ShowQuantAnalysisTablet from "../Tablet/Pages/ShowQuantAnalysis/ShowQuantAnalysis";
import ShowQuantAnalysisDetailsTablet from "../Tablet/Pages/ShowQuantAnalysisDetails/ShowQuantAnalysisDetails";
import QuantAnalysisUploadTablet from "../Tablet/Pages/QuantAnalysisUpload/QuantAnalysisUpload";

const PortfolioIntelligence = React.lazy(() => import("../Components/PortfolioIntelligence/PortfolioIntelligence"));

/* ========================================================================
 * Lazy-loaded route components — desktop / mobile / tablet trio per page
 * ====================================================================== */
const lazyTrio = (
  desktop: () => Promise<{ default: any }>,
  mobile: () => Promise<{ default: any }>,
  tablet: () => Promise<{ default: any }>
) => ({
  Desktop: React.lazy(desktop),
  Mobile: React.lazy(mobile),
  Tablet: React.lazy(tablet),
});

/* eslint-disable @typescript-eslint/no-explicit-any */
const Pages = {
  CapitalMarkets: lazyTrio(
    () => import("../Components/Main/HomePage/Dashboard/CapitalMarkets"),
    () => import("../Mobile/Pages/CapitalMarkets/CapitalMarkets"),
    () => import("../Tablet/Pages/CapitalMarkets/CapitalMarkets")
  ),
  MonasheeDeals: lazyTrio(
    () => import("../Components/Main/HomePage/Dashboard/MonasheeDeals"),
    () => import("../Mobile/Pages/MonasheeDeals/MonasheeDeals"),
    () => import("../Tablet/Pages/MonasheeDeals/MonasheeDeals")
  ),
  InvestmentMain: lazyTrio(
    () => import("../Components/Main/InvestmentStrategy/InvestmentMain"),
    () => import("../Mobile/Pages/InvestmentMain/InvestmentMain"),
    () => import("../Tablet/Pages/InvestmentMain/InvestmentMain")
  ),
  TechnicalMain: lazyTrio(
    () => import("../Components/Main/InvestmentStrategy/TechnicalIndicators/TechnicalMain"),
    () => import("../Mobile/Pages/TechnicalMain/TechnicalMain"),
    () => import("../Tablet/Pages/TechnicalMain/TechnicalMain")
  ),
  DetailedGapData: lazyTrio(
    () => import("../Components/Main/MonasheeDeals/MDDSettings/DetailedGapData"),
    () => import("../Mobile/Pages/DetailedGapData/DetailedGapData"),
    () => import("../Tablet/Pages/DetailedGapData/DetailedGapData")
  ),
  GroupedGapData: lazyTrio(
    () => import("../Components/Main/MonasheeDeals/MDDSettings/GroupedGapData"),
    () => import("../Components/Main/MonasheeDeals/MDDSettings/GroupedGapData"),
    () => import("../Components/Main/MonasheeDeals/MDDSettings/GroupedGapData")
  ),
  RollingGapReport: lazyTrio(
    () => import("../Components/Main/MonasheeDeals/MDDSettings/RollingGapReport"),
    () => import("../Components/Main/MonasheeDeals/MDDSettings/RollingGapReport"),
    () => import("../Components/Main/MonasheeDeals/MDDSettings/RollingGapReport")
  ),
  GatorRunHistoryDashboard: lazyTrio(
    () => import("../Components/GatorPostIpoAgent/GatorRunHistoryDashboard"),
    () => import("../Components/GatorPostIpoAgent/GatorRunHistoryDashboard"),
    () => import("../Components/GatorPostIpoAgent/GatorRunHistoryDashboard")
  ),
  Logs: lazyTrio(
    () => import("../Components/Main/HomePage/Authentication/Logs"),
    () => import("../Mobile/Pages/Logs/Logs"),
    () => import("../Tablet/Pages/Logs/Logs")
  ),
  DealStats: lazyTrio(
    () => import("../Components/Main/MonasheeDeals/MddGraphs/DealStats"),
    () => import("../Mobile/Pages/DealStats/DealStats"),
    () => import("../Tablet/Pages/DealStats/DealStats")
  ),
  FOllowOnDiscount: lazyTrio(
    () => import("../Components/Main/MonasheeDeals/MddGraphs/FOllowOnDiscount"),
    () => import("../Mobile/Pages/FOllowOnDiscount/FOllowOnDiscount"),
    () => import("../Tablet/Pages/FOllowOnDiscount/FOllowOnDiscount")
  ),
  MDDScreener: lazyTrio(
    () => import("../Components/Main/MonasheeDeals/MddGraphs/MDDScreener"),
    () => import("../Mobile/Pages/MDDScreener/MDDScreener"),
    () => import("../Tablet/Pages/MDDScreener/MDDScreener")
  ),
  AllocationCaptureReturn: lazyTrio(
    () => import("../Components/Main/MonasheeDeals/MddGraphs/AllocationCaptureReturn"),
    () => import("../Mobile/Pages/AllocationCaptureReturn/AllocationCaptureReturn"),
    () => import("../Tablet/Pages/AllocationCaptureReturn/AllocationCaptureReturn")
  ),
  BankTable: lazyTrio(
    () => import("../Components/Main/MonasheeDeals/MDDSettings/BankTable"),
    () => import("../Mobile/Pages/BankTable/BankTable"),
    () => import("../Tablet/Pages/BankTable/BankTable")
  ),
  FundWiseTable: lazyTrio(
    () => import("../Components/Main/PortfolioAttribution/FundwiseTable"),
    () => import("../Mobile/Pages/FundWiseTable/FundWiseTable"),
    () => import("../Tablet/Pages/FundWiseTable/FundWiseTable")
  ),
  HighYieldsMain: lazyTrio(
    () => import("../Components/HighYields/HighYieldsMain"),
    () => import("../Mobile/Pages/HighYieldsMain/HighYieldsMain"),
    () => import("../Tablet/Pages/HighYieldsMain/HighYieldsMain")
  ),
  DealStatsMain: lazyTrio(
    () => import("../Components/HighYields/Tabs/DealStatsMain"),
    () => import("../Mobile/Pages/DealStatsMain/DealStatsMain"),
    () => import("../Tablet/Pages/DealStatsMain/DealStatsMain")
  ),
  HYSkewTableMain: lazyTrio(
    () => import("../Components/HighYields/Tabs/HYSkewTableMain"),
    () => import("../Mobile/Pages/HYSkewTableMain/HYSkewTableMain"),
    () => import("../Tablet/Pages/HYSkewTableMain/HYSkewTableMain")
  ),
  HYTransactionsMain: lazyTrio(
    () => import("../Components/HighYields/Tabs/HYTransactionsMain"),
    () => import("../Components/HighYields/Tabs/HYTransactionsMain"),
    () => import("../Components/HighYields/Tabs/HYTransactionsMain")
  ),
  MDDSelectedTicker: lazyTrio(
    () => import("../Components/Main/MonasheeDeals/MddGraphs/MDDSelectedTicker"),
    () => import("../Mobile/Pages/MDDSelectedTicker/MDDSelectedTicker"),
    () => import("../Tablet/Pages/MDDSelectedTicker/MDDSelectedTicker")
  ),
  MarketFilters: lazyTrio(
    () => import("../Components/Main/MonasheeCapitalMarkets/MarketFilters"),
    () => import("../Mobile/Pages/MarketFilters/MarketFilters"),
    () => import("../Tablet/Pages/MarketFilters/MarketFilters")
  ),
  ScreenerMain: lazyTrio(
    () => import("../Components/Main/MonasheeGraphs/ScreenerTable/ScreenerMain"),
    () => import("../Mobile/Pages/ScreenerMain/ScreenerMain"),
    () => import("../Tablet/Pages/ScreenerMain/ScreenerMain")
  ),
  SkewTableMain: lazyTrio(
    () => import("../Components/Main/MonasheeGraphs/SkewTableMain"),
    () => import("../Mobile/Pages/SkewTableMain/SkewTableMain"),
    () => import("../Tablet/Pages/SkewTableMain/SkewTableMain")
  ),
  MacroMain: lazyTrio(
    () => import("../Components/Macro/MacroMain"),
    () => import("../Mobile/Pages/MacroMain/MacroMain"),
    () => import("../Tablet/Pages/MacroMain/MacroMain")
  ),
  StockTickerNews: lazyTrio(
    () => import("../Components/Macro/StockTickerNews"),
    () => import("../Mobile/Pages/StockTickerNews/StockTickerNews"),
    () => import("../Tablet/Pages/StockTickerNews/StockTickerNews")
  ),
  ConvertsMain: lazyTrio(
    () => import("../Components/Converts/ConvertsMain"),
    () => import("../Mobile/Pages/ConvertsMain/ConvertsMain"),
    () => import("../Tablet/Pages/ConvertsMain/ConvertsMain")
  ),
  ConvertsDealStatsMain: lazyTrio(
    () => import("../Components/Converts/Tabs/ConvertsDealStatsMain"),
    () => import("../Mobile/Pages/ConvertsDealStatsMain/ConvertsDealStatsMain"),
    () => import("../Tablet/Pages/ConvertsDealStatsMain/ConvertsDealStatsMain")
  ),
  ConvertsSkewMain: lazyTrio(
    () => import("../Components/Converts/Tabs/ConvertsSkewMain"),
    () => import("../Mobile/Pages/ConvertsSkewMain/ConvertsSkewMain"),
    () => import("../Tablet/Pages/ConvertsSkewMain/ConvertsSkewMain")
  ),
  ConvertsTransactionsMain: lazyTrio(
    () => import("../Components/Converts/Tabs/ConvertsTransactionsMain"),
    () => import("../Components/Converts/Tabs/ConvertsTransactionsMain"),
    () => import("../Components/Converts/Tabs/ConvertsTransactionsMain")
  ),
  MainUpload: lazyTrio(
    () => import("../Components/Uploads/MainUpload"),
    () => import("../Mobile/Pages/MainUpload/MainUpload"),
    () => import("../Tablet/Pages/MainUpload/MainUpload")
  ),
  AumUpdate: lazyTrio(
    () => import("../Components/Uploads/AumUpdate"),
    () => import("../Components/Uploads/AumUpdate"),
    () => import("../Components/Uploads/AumUpdate")
  ),
  PortfolioAumUpdate: lazyTrio(
    () => import("../Components/Uploads/PortfolioAumUpdate"),
    () => import("../Components/Uploads/PortfolioAumUpdate"),
    () => import("../Components/Uploads/PortfolioAumUpdate")
  ),
  ModelPortfolioPositionUpload: lazyTrio(
    () => import("../Components/Uploads/ModelPortfolioPositionUpload"),
    () => import("../Components/Uploads/ModelPortfolioPositionUpload"),
    () => import("../Components/Uploads/ModelPortfolioPositionUpload")
  ),
  FundamentalsTechnical: lazyTrio(
    () => import("../Components/Uploads/FundamentalsTechnical"),
    () => import("../Mobile/Pages/FundamentalsTechnical/FundamentalsTechnical"),
    () => import("../Tablet/Pages/FundamentalsTechnical/FundamentalsTechnical")
  ),
  LandingPageMain: lazyTrio(
    () => import("../Components/Main/DashBoards/LandingPageMain"),
    () => import("../Mobile/Pages/LandingPageMain/LandingPageMain"),
    () => import("../Tablet/Pages/LandingPageMain/LandingPageMain")
  ),
  OperationsDashboard: lazyTrio(
    () => import("../Components/Main/DashBoards/OperationsDashboard"),
    () => import("../Mobile/Pages/OperationsDashboard/OperationsDashboard"),
    () => import("../Tablet/Pages/OperationsDashboard/OperationsDashboard")
  ),
  IncrementalFundamentalUpdate: lazyTrio(
    () => import("../Components/Main/DashBoards/IncrementalFundamentalUpdate"),
    () => import("../Components/Main/DashBoards/IncrementalFundamentalUpdate"),
    () => import("../Components/Main/DashBoards/IncrementalFundamentalUpdate")
  ),
  DailyReportPost: lazyTrio(
    () => import("../Components/Main/WriteUpsRecords/DailyReportPost"),
    () => import("../Mobile/Pages/DailyReportPost/DailyReportPost"),
    () => import("../Tablet/Pages/DailyReportPost/DailyReportPost")
  ),
  ReportWriteUpMain: lazyTrio(
    () => import("../Components/Main/WriteUpsRecords/ReportWriteUpMain"),
    () => import("../Mobile/Pages/ReportWriteUpMain/ReportWriteUpMain"),
    () => import("../Tablet/Pages/ReportWriteUpMain/ReportWriteUpMain")
  ),
  CombinedSelectedTicker: lazyTrio(
    () => import("../Components/Main/MonasheeGraphs/CombinedSelectedTicker"),
    () => import("../Mobile/Pages/CombinedSelectedTicker/CombinedSelectedTicker"),
    () => import("../Tablet/Pages/CombinedSelectedTicker/CombinedSelectedTicker")
  ),
  MarketOpportnuityMain: lazyTrio(
    () => import("../Components/Main/HomePage/Dashboard/MarketOpportnuityMain"),
    () => import("../Mobile/Pages/MarketOpportnuityMain/MarketOpportnuityMain"),
    () => import("../Tablet/Pages/MarketOpportnuityMain/MarketOpportnuityMain")
  ),
  WeeklyMain: lazyTrio(
    () => import("../Components/Main/MonasheeDeals/MDDSettings/WeeklyMain"),
    () => import("../Mobile/Pages/WeeklyMain/WeeklyMain"),
    () => import("../Tablet/Pages/WeeklyMain/WeeklyMain")
  ),
  DeatiledRegionPnlAttribution: lazyTrio(
    () => import("../Components/PNLAttribution/DeatiledRegionPnlAttribution"),
    () => import("../Mobile/Pages/DeatiledRegionPnlAttribution/DeatiledRegionPnlAttribution"),
    () => import("../Tablet/Pages/DeatiledRegionPnlAttribution/DeatiledRegionPnlAttribution")
  ),
  EquityNewDealFormMain: lazyTrio(
    () => import("../Components/EquityNewDealFormMain/EquityNewDealFormMain"),
    () => import("../Mobile/Pages/EquityNewDealFormMain/EquityNewDealFormMain"),
    () => import("../Tablet/Pages/EquityNewDealFormMain/EquityNewDealFormMain")
  ),
  LkFileUpload: lazyTrio(
    () => import("../Components/Uploads/LkFileUpload"),
    () => import("../Mobile/Pages/LkFileUpload/LkFileUpload"),
    () => import("../Tablet/Pages/LkFileUpload/LkFileUpload")
  ),
  DetailedDealsView: lazyTrio(
    () => import("../Components/Main/MonasheeGraphs/ScreenerTable/DetailedDealsView"),
    () => import("../Mobile/Pages/DetailedDealsView/DetailedDealsView"),
    () => import("../Tablet/Pages/DetailedDealsView/DetailedDealsView")
  ),
  DetailedLeadBankView: lazyTrio(
    () => import("../Components/Main/MonasheeGraphs/ScreenerTable/DetailedLeadBankView"),
    () => import("../Mobile/Pages/DetailedLeadBankView/DetailedLeadBankView"),
    () => import("../Tablet/Pages/DetailedLeadBankView/DetailedLeadBankView")
  ),
  PNLTabMain: lazyTrio(
    () => import("../Components/PNLAttribution/PNLTabMain"),
    () => import("../Mobile/Pages/PNLTabMain/PNLTabMain"),
    () => import("../Tablet/Pages/PNLTabMain/PNLTabMain")
  ),
  DetailedRegionView: lazyTrio(
    () => import("../Components/Main/MonasheeGraphs/ScreenerTable/DetailedRegionView"),
    () => import("../Mobile/Pages/DetailedRegionView/DetailedRegionView"),
    () => import("../Tablet/Pages/DetailedRegionView/DetailedRegionView")
  ),
  UploadAiInsights: lazyTrio(
    () => import("../Components/Main/DashBoards/InsightsAi/UploadsInsights/UploadAiInsights"),
    () => import("../Mobile/Pages/UploadAiInsights/UploadAiInsights"),
    () => import("../Tablet/Pages/UploadAiInsights/UploadAiInsights")
  ),
  VersionUploadForm: lazyTrio(
    () => import("../Components/Uploads/DailyMonasheeUploads/VersionUploadForm"),
    () => import("../Mobile/Pages/VersionUploadForm/VersionUploadForm"),
    () => import("../Tablet/Pages/VersionUploadForm/VersionUploadForm")
  ),
  Agents: lazyTrio(
    () => import("../Components/Agents/Agents"),
    () => import("../Mobile/Pages/Agents/Agents"),
    () => import("../Tablet/Pages/Agents/Agents")
  ),
  AgentOutputView: lazyTrio(
    () => import("../Components/Agents/AgentOutputView"),
    () => import("../Mobile/Pages/AgentOutputView/AgentOutputView"),
    () => import("../Tablet/Pages/AgentOutputView/AgentOutputView")
  ),
  AgentTasksMain: lazyTrio(
    () => import("../Components/Agents/AgentTasksMain"),
    () => import("../Mobile/Pages/AgentTasksMain/AgentTasksMain"),
    () => import("../Tablet/Pages/AgentTasksMain/AgentTasksMain")
  ),
  AgentTickerOverview: lazyTrio(
    () => import("../Components/Agents/AgentTickerOverview"),
    () => import("../Mobile/Pages/AgentTickerOverview/AgentTickerOverview"),
    () => import("../Tablet/Pages/AgentTickerOverview/AgentTickerOverview")
  ),
  BetaTransferMain: lazyTrio(
    () => import("../Components/BetaTransfer/BetaTransferMain"),
    () => import("../Mobile/Pages/BetaTransferMain/BetaTransferMain"),
    () => import("../Tablet/Pages/BetaTransferMain/BetaTransferMain")
  ),
  DealDetailedGapAnalysis: lazyTrio(
    () => import("../Components/Main/DealDetailedGapAnalysis"),
    () => import("../Mobile/Pages/DealDetailedGapAnalysis/DealDetailedGapAnalysis"),
    () => import("../Tablet/Pages/DealDetailedGapAnalysis/DealDetailedGapAnalysis")
  ),
  PerplexityChatMain: lazyTrio(
    () => import("../Components/GhcAi/PerplexityChatMain"),
    () => import("../Mobile/Pages/PerplexityChatMain/PerplexityChatMain"),
    () => import("../Tablet/Pages/PerplexityChatMain/PerplexityChatMain")
  ),
  HeatMapMain: lazyTrio(
    () => import("../Components/GhcAi/AIPages/HeatMap/HeatMapMain"),
    () => import("../Mobile/Pages/HeatMapMain/HeatMapMain"),
    () => import("../Tablet/Pages/HeatMapMain/HeatMapMain")
  ),
  EquityDealsIPOFO: lazyTrio(
    () => import("../Components/Main/DashBoards/EquityDealsIPOFO"),
    () => import("../Mobile/Pages/EquityDealsIPOFO/EquityDealsIPOFO"),
    () => import("../Tablet/Pages/EquityDealsIPOFO/EquityDealsIPOFO")
  ),
  ExportUnifiedDealData: lazyTrio(
    () => import("../Components/Main/UnifiedDealsDataMain/ExportUnifiedDealData"),
    () => import("../Mobile/Pages/ExportUnifiedDealData/ExportUnifiedDealData"),
    () => import("../Tablet/Pages/ExportUnifiedDealData/ExportUnifiedDealData")
  ),
  UnifiedDealDataUpload: lazyTrio(
    () => import("../Components/Main/UnifiedDealsDataMain/DesignUiPath/UnifiedDealDataUpload"),
    () => import("../Mobile/Pages/UnifiedDealDataUpload/UnifiedDealDataUpload"),
    () => import("../Tablet/Pages/UnifiedDealDataUpload/UnifiedDealDataUpload")
  ),
  TickerDashboard: lazyTrio(
    () => import("../Components/DealTracking/TickerDashboard"),
    () => import("../Mobile/Pages/TickerDashboard/TickerDashboard"),
    () => import("../Tablet/Pages/TickerDashboard/TickerDashboard")
  ),
  UploadsWriteUpMain: lazyTrio(
    () => import("../Components/Main/WriteUpsRecords/UploadsWriteUpMain"),
    () => import("../Mobile/Pages/UploadsWriteUpMain/UploadsWriteUpMain"),
    () => import("../Tablet/Pages/UploadsWriteUpMain/UploadsWriteUpMain")
  ),
  NewDealsCycleMain: lazyTrio(
    () => import("../Components/Main/NewDealsLifeCycle/NewDealsCycleMain"),
    () => import("../Mobile/Pages/NewDealsCycleMain/NewDealsCycleMain"),
    () => import("../Tablet/Pages/NewDealsCycleMain/NewDealsCycleMain")
  ),
  TickerTrackingWrapper: lazyTrio(
    () => import("../Components/DealTracking/TickerTrackingWrapper"),
    () => import("../Mobile/Pages/TickerTrackingWrapper/TickerTrackingWrapper"),
    () => import("../Tablet/Pages/TickerTrackingWrapper/TickerTrackingWrapper")
  ),
  FOWriteUpMain: lazyTrio(
    () => import("../Components/Main/FOWriteUpMain/FOWriteUpMain"),
    () => import("../Mobile/Pages/FOWriteUpMain/FOWriteUpMain"),
    () => import("../Tablet/Pages/FOWriteUpMain/FOWriteUpMain")
  ),
  FOFinancialForecastUpload: lazyTrio(
    () => import("../Components/Main/FOWriteUpMain/FOWriteUpUploads/FOFinancialForecastUpload"),
    () => import("../Mobile/Pages/FOFinancialForecastUpload/FOFinancialForecastUpload"),
    () => import("../Tablet/Pages/FOFinancialForecastUpload/FOFinancialForecastUpload")
  ),
  DealsTabsLayout: lazyTrio(
    () => import("../Components/Main/UnifiedDealsDataMain/DesignUiPath/DealsTabsLayout"),
    () => import("../Components/Main/UnifiedDealsDataMain/DesignUiPath/DealsTabsLayout"),
    () => import("../Components/Main/UnifiedDealsDataMain/DesignUiPath/DealsTabsLayout")
  ),
  EquityAiMlPage: lazyTrio(
    () => import("../Components/AIML/EquityAiMlPage"),
    () => import("../Mobile/Pages/EquityAiMlPage/EquityAiMlPage"),
    () => import("../Tablet/Pages/EquityAiMlPage/EquityAiMlPage")
  ),
  NewDashboardLifeCycleDetails: lazyTrio(
    () => import("../Components/NewDashboardLifeCycle/NewDashboardLifeCycleDetails"),
    () => import("../Mobile/Pages/NewDashboardLifeCycleDetails/NewDashboardLifeCycleDetails"),
    () => import("../Tablet/Pages/NewDashboardLifeCycleDetails/NewDashboardLifeCycleDetails")
  ),
  WriteUpIPODashbaord: lazyTrio(
    () => import("../Components/IPOwriteUp/IPOWriteUpDashboard/WriteUpIPODashbaord"),
    () => import("../Mobile/Pages/WriteUpIPODashbaord/WriteUpIPODashbaord"),
    () => import("../Tablet/Pages/WriteUpIPODashbaord/WriteUpIPODashbaord")
  ),
  UploadFactSetTickers: lazyTrio(
    () => import("../Components/Uploads/DailyMonasheeUploads/UploadFactSetTickers"),
    () => import("../Mobile/Pages/UploadFactSetTickers/UploadFactSetTickers"),
    () => import("../Tablet/Pages/UploadFactSetTickers/UploadFactSetTickers")
  ),
  FSDealUnifiedMain: lazyTrio(
    () => import("../Components/Main/UnifiedDealsDataMain/DesignUiPath/FactsetDataDetails/FSDealUnifiedMain"),
    () => import("../Mobile/Pages/FSDealUnifiedMain/FSDealUnifiedMain"),
    () => import("../Tablet/Pages/FSDealUnifiedMain/FSDealUnifiedMain")
  ),
  DeleteUnifiedDealData: lazyTrio(
    () => import("../Components/Main/UnifiedDealsDataMain/DeleteUnifiedDealData"),
    () => import("../Mobile/Pages/DeleteUnifiedDealData/DeleteUnifiedDealData"),
    () => import("../Tablet/Pages/DeleteUnifiedDealData/DeleteUnifiedDealData")
  ),
  DailyNoteDeleteTickersData: lazyTrio(
    () => import("../Components/Uploads/DailyNoteDeleteTickersData"),
    () => import("../Mobile/Pages/DailyNoteDeleteTickersData/DailyNoteDeleteTickersData"),
    () => import("../Tablet/Pages/DailyNoteDeleteTickersData/DailyNoteDeleteTickersData")
  ),
  IPOUploadsPage: lazyTrio(
    () => import("../Components/IPOwriteUp/IPOUploadsPage"),
    () => import("../Mobile/Pages/IPOUploadsPage/IPOUploadsPage"),
    () => import("../Tablet/Pages/IPOUploadsPage/IPOUploadsPage")
  ),
  ExcelUploads: lazyTrio(
    () => import("../Components/UpcomingPipelineDeals/ExcelUploads"),
    () => import("../Mobile/Pages/ExcelUploads/ExcelUploads"),
    () => import("../Tablet/Pages/ExcelUploads/ExcelUploads")
  ),
  ABBModelTabs: lazyTrio(
    () => import("../Components/ABBModelMain/ABBModelTabs"),
    () => import("../Components/ABBModelMain/ABBModelTabs"),
    () => import("../Components/ABBModelMain/ABBModelTabs")
  ),
  VALDModelMain: lazyTrio(
    () => import("../Components/ABBModelMain/VALDModel/VALDModelMain"),
    () => import("../Components/ABBModelMain/VALDModel/VALDModelMain"),
    () => import("../Components/ABBModelMain/VALDModel/VALDModelMain")
  ),
  ConvertibleBondModelPage: lazyTrio(
    () => import("../Components/ConvertibleBondModel/ConvertibleBondModelPage"),
    () => import("../Components/ConvertibleBondModel/ConvertibleBondModelPage"),
    () => import("../Components/ConvertibleBondModel/ConvertibleBondModelPage")
  ),
  AIMLResultsHome: lazyTrio(
    () => import("../Components/AIMLResults/AIMLResultsHome"),
    () => import("../Mobile/Pages/AIMLResultsHome/AIMLResultsHome"),
    () => import("../Tablet/Pages/AIMLResultsHome/AIMLResultsHome")
  ),
  USDashboard: lazyTrio(
    () => import("../Components/USDashboard/USDashboard"),
    () => import("../Components/USDashboard/USDashboard"),
    () => import("../Components/USDashboard/USDashboard")
  ),
  MattermostChat: lazyTrio(
    () => import("../Components/Discussion/MattermostChat"),
    () => import("../Mobile/Pages/MattermostChat/MattermostChat"),
    () => import("../Tablet/Pages/MattermostChat/MattermostChat")
  ),
  MDRMainTableS3Data: lazyTrio(
    () => import("../Components/MDRMainS3Data/MDRMainTableS3Data"),
    () => import("../Mobile/Pages/MDRMainTableS3Data/MDRMainTableS3Data"),
    () => import("../Tablet/Pages/MDRMainTableS3Data/MDRMainTableS3Data")
  ),
  SentimentAnalysisTabs: lazyTrio(
    () => import("../Components/AIML/SentimentAnalysisTabs"),
    () => import("../Mobile/Pages/SentimentAnalysisTabs/SentimentAnalysisTabs"),
    () => import("../Tablet/Pages/SentimentAnalysisTabs/SentimentAnalysisTabs")
  ),
  FewShotAnalysisUpload: lazyTrio(
    () => import("../Components/AIFewshotAnalysis/FewShotAnalysisUpload"),
    () => import("../Mobile/Pages/FewShotAnalysisUpload/FewShotAnalysisUpload"),
    () => import("../Tablet/Pages/FewShotAnalysisUpload/FewShotAnalysisUpload")
  ),
  AIFewshotAnalysis: lazyTrio(
    () => import("../Components/AIFewshotAnalysis/AIFewshotAnalysis"),
    () => import("../Mobile/Pages/AIFewshotAnalysis/AIFewshotAnalysis"),
    () => import("../Tablet/Pages/AIFewshotAnalysis/AIFewshotAnalysis")
  ),
  ShowUSSentimentAnalysis: lazyTrio(
    () => import("../Components/AIML/ShowUSSentimentAnalysis"),
    () => import("../Mobile/Pages/ShowUSSentimentAnalysis/ShowUSSentimentAnalysis"),
    () => import("../Tablet/Pages/ShowUSSentimentAnalysis/ShowUSSentimentAnalysis")
  ),
  NotesUI: lazyTrio(
    () => import("../Components/mattermostupload/NotesUI"),
    () => import("../Mobile/Pages/NotesUI/NotesUI"),
    () => import("../Tablet/Pages/NotesUI/NotesUI")
  ),
  APACEquityAiMlPage: lazyTrio(
    () => import("../Components/AIML/APACEquityAIMLPage"),
    () => import("../Mobile/Pages/APACEquityAiMlPage/APACEquityAiMlPage"),
    () => import("../Tablet/Pages/APACEquityAiMlPage/APACEquityAiMlPage")
  ),
  DealMeetingNotesMain: lazyTrio(
    () => import("../Components/Main/DealMeetingNotes/DealMeetingNotesMain"),
    () => import("../Mobile/Pages/DealMeetingNotesMain/DealMeetingNotesMain"),
    () => import("../Tablet/Pages/DealMeetingNotesMain/DealMeetingNotesMain")
  ),
  NewDashboardLifeCycleMain: lazyTrio(
    () => import("../Components/NewDashboardLifeCycle/NewDashboardLifeCycleMain"),
    () => import("../Mobile/Pages/NewDashboardLifeCycleMain/NewDashboardLifeCycleMain"),
    () => import("../Tablet/Pages/NewDashboardLifeCycleMain/NewDashboardLifeCycleMain")
  ),
  HotDealsMain: lazyTrio(
    () => import("../Components/HotDeals/HotDealsMain"),
    () => import("../Components/HotDeals/HotDealsMain"),
    () => import("../Components/HotDeals/HotDealsMain")
  ),
  HotDealsAdminMain: lazyTrio(
    () => import("../Components/HotDeals/HotDealsAdminMain"),
    () => import("../Components/HotDeals/HotDealsAdminMain"),
    () => import("../Components/HotDeals/HotDealsAdminMain")
  ),
  FuturePipelineDealsMain: lazyTrio(
    () => import("../Components/UpcomingPipelineDeals/FuturePipelineDealsMain"),
    () => import("../Mobile/Pages/FuturePipelineDealsMain/FuturePipelineDealsMain"),
    () => import("../Tablet/Pages/FuturePipelineDealsMain/FuturePipelineDealsMain")
  ),
  TickerChange: lazyTrio(
    () => import("../Components/TickerChange/TickerChange"),
    () => import("../Mobile/Pages/TickerChange/TickerChange"),
    () => import("../Tablet/Pages/TickerChange/TickerChange")
  ),
  NewDashboardFOLifeCycleDetails: lazyTrio(
    () => import("../Components/NewDashboardLifeCycle/NewDashboardFOLifeCycleDetails"),
    () => import("../Mobile/Pages/NewDashboardFOLifeCycleDetails/NewDashboardFOLifeCycleDetails"),
    () => import("../Tablet/Pages/NewDashboardFOLifeCycleDetails/NewDashboardFOLifeCycleDetails")
  ),
  SignalBoardMain: lazyTrio(
    () => import("../Components/SignalBoard/SignalBoardMain"),
    () => import("../Mobile/Pages/SignalBoardMain/SignalBoardMain"),
    () => import("../Tablet/Pages/SignalBoardMain/SignalBoardMain")
  ),
  NewPortfolioRiskUpload: lazyTrio(
    () => import("../Components/Uploads/NewPortfolioRiskUpload"),
    () => import("../Mobile/Pages/NewPortfolioRiskUpload/NewPortfolioRiskUpload"),
    () => import("../Tablet/Pages/NewPortfolioRiskUpload/NewPortfolioRiskUpload")
  ),
  UsIndexReturnUpload: lazyTrio(
    () => import("../Components/Uploads/UsIndexReturnUpload"),
    () => import("../Mobile/Pages/UsIndexReturnUpload/UsIndexReturnUpload"),
    () => import("../Tablet/Pages/UsIndexReturnUpload/UsIndexReturnUpload")
  ),
  RiskDashboard: lazyTrio(
    () => import("../Components/NewPNLAttributionRiskReport/PNLAttributionData/RiskDashboard"),
    () => import("../Mobile/Pages/RiskDashboard/RiskDashboard"),
    () => import("../Tablet/Pages/RiskDashboard/RiskDashboard")
  ),
  TickerDetail: lazyTrio(
    () => import("../Components/NewPNLAttributionRiskReport/PNLAttributionData/TickerDetail"),
    () => import("../Mobile/Pages/TickerDetail/TickerDetail"),
    () => import("../Tablet/Pages/TickerDetail/TickerDetail")
  ),
  RiskTriggers: lazyTrio(
    () => import("../Components/NewPNLAttributionRiskReport/PNLAttributionData/RiskTriggers"),
    () => import("../Mobile/Pages/RiskTriggers/RiskTriggers"),
    () => import("../Tablet/Pages/RiskTriggers/RiskTriggers")
  ),
  RiskSummary: lazyTrio(
    () => import("../Components/NewPNLAttributionRiskReport/PNLAttributionData/RiskSummary"),
    () => import("../Mobile/Pages/RiskSummary/RiskSummary"),
    () => import("../Tablet/Pages/RiskSummary/RiskSummary")
  ),
  RiskAIDocumentUpload: lazyTrio(
    () => import("../Components/NewPNLAttributionRiskReport/RiskReportNew/RiskAIDocumentUpload"),
    () => import("../Mobile/Pages/RiskAIDocumentUpload/RiskAIDocumentUpload"),
    () => import("../Tablet/Pages/RiskAIDocumentUpload/RiskAIDocumentUpload")
  ),
  DocumentUploadTabs: lazyTrio(
    () => import("../Components/NewPNLAttributionRiskReport/RiskReportNew/DocumentUploadTabs"),
    () => import("../Mobile/Pages/DocumentUploadTabs/DocumentUploadTabs"),
    () => import("../Tablet/Pages/DocumentUploadTabs/DocumentUploadTabs")
  ),
  AIPortfolioReview: lazyTrio(
    () => import("../Components/NewPNLAttributionRiskReport/RiskReportNew/AIPortfolioReview"),
    () => import("../Mobile/Pages/AIPortfolioReview/AIPortfolioReview"),
    () => import("../Tablet/Pages/AIPortfolioReview/AIPortfolioReview")
  ),
  JayRitterIPOAnalysis: lazyTrio(
    () => import("../Components/JayRitter/JayRitterIPOAnalysis"),
    () => import("../Mobile/Pages/JayRitterIPOAnalysis/JayRitterIPOAnalysis"),
    () => import("../Tablet/Pages/JayRitterIPOAnalysis/JayRitterIPOAnalysis")
  ),
  SentimentSummary: lazyTrio(
    () => import("../Components/AIML/SentimentSummary"),
    () => import("../Mobile/Pages/SentimentSummary/SentimentSummary"),
    () => import("../Tablet/Pages/SentimentSummary/SentimentSummary")
  ),
  UnsupervisedDealSummary: lazyTrio(
    () => import("../Components/AIML/UnsupervisedDealSummary"),
    () => import("../Mobile/Pages/UnsupervisedDealSummary/UnsupervisedDealSummary"),
    () => import("../Tablet/Pages/UnsupervisedDealSummary/UnsupervisedDealSummary")
  ),
  PortfolioSummaryTabs: lazyTrio(
    () => import("../Components/IPODashboardLLM/PortfolioSummaryTabs"),
    () => import("../Mobile/Pages/PortfolioSummaryTabs/PortfolioSummaryTabs"),
    () => import("../Tablet/Pages/PortfolioSummaryTabs/PortfolioSummaryTabs")
  ),
  UploadClaudeSentiment: lazyTrio(
    () => import("../Components/AIML/UploadClaudeSentiment"),
    () => import("../Mobile/Pages/UploadClaudeSentiment/UploadClaudeSentiment"),
    () => import("../Tablet/Pages/UploadClaudeSentiment/UploadClaudeSentiment")
  ),
  SummarySignalBoard: lazyTrio(
    () => import("../Components/SummarySignalBoard/SummarySignalBoard"),
    () => import("../Mobile/Pages/SummarySignalBoard/SummarySignalBoard"),
    () => import("../Tablet/Pages/SummarySignalBoard/SummarySignalBoard")
  ),
  S3DataTransfer: lazyTrio(
    () => import("../Components/S3DataTransfer/S3DataTransfer"),
    () => import("../Mobile/Pages/S3DataTransfer/S3DataTransfer"),
    () => import("../Tablet/Pages/S3DataTransfer/S3DataTransfer")
  ),
  DataDump: lazyTrio(
    () => import("../Components/DataDump/DataDump"),
    () => import("../Mobile/Pages/DataDump/DataDump"),
    () => import("../Tablet/Pages/DataDump/DataDump")
  ),
  JRitterAgentMain: lazyTrio(
    () => import("../Components/JRitterAgent/JRitterAgentMain"),
    () => import("../Mobile/Pages/JRitterAgentMain/JRitterAgentMain"),
    () => import("../Tablet/Pages/JRitterAgentMain/JRitterAgentMain")
  ),
  JUploadPage: lazyTrio(
    () => import("../Components/JRitterAgent/JUploadPage"),
    () => import("../Mobile/Pages/JUploadPage/JUploadPage"),
    () => import("../Tablet/Pages/JUploadPage/JUploadPage")
  ),
  PortfolioAgentMain: lazyTrio(
    () => import("../Components/Agents/PortfolioAgentMain"),
    () => import("../Components/Agents/PortfolioAgentMain"),
    () => import("../Components/Agents/PortfolioAgentMain")
  ),
  GatorPostIpoAgentMain: lazyTrio(
    () => import("../Components/GatorPostIpoAgent/GatorPostIpoAgentMain"),
    () => import("../Mobile/Pages/GatorPostIpoAgentMain/GatorPostIpoAgentMain"),
    () => import("../Tablet/Pages/GatorPostIpoAgentMain/GatorPostIpoAgentMain")
  ),
  GatorPostIpoUpload: lazyTrio(
    () => import("../Components/GatorPostIpoAgent/GatorPostIpoUpload"),
    () => import("../Mobile/Pages/GatorPostIpoUpload/GatorPostIpoUpload"),
    () => import("../Tablet/Pages/GatorPostIpoUpload/GatorPostIpoUpload")
  ),
  IpoGatorRunDashboard: lazyTrio(
    () => import("../Components/GatorPostIpoAgent/IpoGatorRunDashboard"),
    () => import("../Mobile/Pages/IpoGatorRunDashboard/IpoGatorRunDashboard"),
    () => import("../Tablet/Pages/IpoGatorRunDashboard/IpoGatorRunDashboard")
  ),
  DatabaseExplorer: lazyTrio(
    () => import("../Components/DatabaseExplorer/DatabaseExplorer"),
    () => import("../Mobile/Pages/DatabaseExplorer/DatabaseExplorer"),
    () => import("../Tablet/Pages/DatabaseExplorer/DatabaseExplorer")
  ),
  CIOPortfolioReviewWizard: lazyTrio(
    () => import("../Components/CIOPortfolioReview/CIOPortfolioReviewWizard"),
    () => import("../Mobile/Pages/CIOPortfolioReviewWizard/CIOPortfolioReviewWizard"),
    () => import("../Tablet/Pages/CIOPortfolioReviewWizard/CIOPortfolioReviewWizard")
  ),
  DealsDataDashboard: lazyTrio(
    () => import("../Components/DealsData/DealsDataDashboard"),
    () => import("../Mobile/Pages/DealsDataDashboard/DealsDataDashboard"),
    () => import("../Tablet/Pages/DealsDataDashboard/DealsDataDashboard")
  ),
  OpenClawChatPage: lazyTrio(
    () => import("../Components/OpenClawChat/OpenClawChatPage"),
    () => import("../Mobile/Pages/OpenClawChatPage/OpenClawChatPage"),
    () => import("../Tablet/Pages/OpenClawChatPage/OpenClawChatPage")
  ),
  // Shared desktop component across all three device variants — the
  // budget admin page is rarely used and doesn't need a tailored layout.
  OpenClawBudgetAdminPage: lazyTrio(
    () => import("../Components/OpenClawChat/OpenClawBudgetAdminPage"),
    () => import("../Components/OpenClawChat/OpenClawBudgetAdminPage"),
    () => import("../Components/OpenClawChat/OpenClawBudgetAdminPage")
  ),
  MonthlyDecDashboard: lazyTrio(
    () => import("../Components/MonthlyDec/MonthlyDecDashboard"),
    () => import("../Mobile/Pages/MonthlyDecDashboard/MonthlyDecDashboard"),
    () => import("../Tablet/Pages/MonthlyDecDashboard/MonthlyDecDashboard")
  ),
  // Monthly TR Sheet dashboard — single shared component across all three
  // device variants for now. If a tailored mobile/tablet layout is added
  // later, swap in `../Mobile/Pages/MonthlyTRSheetDashboard/MonthlyTRSheetDashboard` etc.
  // Wrapped in MonthlyTearSheetSwitcher so the IR "Monthly Tear Sheet" page
  // exposes Old (this dashboard) / New (Tear Sheet Template) toggle buttons.
  MonthlyTRSheetDashboard: lazyTrio(
    () => import("../Components/MonthlyTRSheet/MonthlyTearSheetSwitcher"),
    () => import("../Components/MonthlyTRSheet/MonthlyTearSheetSwitcher"),
    () => import("../Components/MonthlyTRSheet/MonthlyTearSheetSwitcher")
  ),
  // Fund Monthly Data dashboard — single shared component across all three
  // device variants for now (same convention as the Monthly TR Sheet above).
  FundMonthlyDataDashboard: lazyTrio(
    () => import("../Components/FundMonthlyData/FundMonthlyDataDashboard"),
    () => import("../Components/FundMonthlyData/FundMonthlyDataDashboard"),
    () => import("../Components/FundMonthlyData/FundMonthlyDataDashboard")
  ),
  // Monthly Fund Reports — single shared component across all three device
  // variants for now (same convention as the Monthly TR Sheet above).
  MonthlyFundReportsDashboard: lazyTrio(
    () => import("../Components/MonthlyFundReports/MonthlyFundReportsDashboard"),
    () => import("../Components/MonthlyFundReports/MonthlyFundReportsDashboard"),
    () => import("../Components/MonthlyFundReports/MonthlyFundReportsDashboard")
  ),
  AdamMonthlyReportDashboard: lazyTrio(
    () => import("../Components/AdamMonthlyReport/AdamMonthlyReportDashboard"),
    () => import("../Components/AdamMonthlyReport/AdamMonthlyReportDashboard"),
    () => import("../Components/AdamMonthlyReport/AdamMonthlyReportDashboard")
  ),
  TechnicalDownload: lazyTrio(
    () => import("../Components/Uploads/TechnicalDownload"),
    () => import("../Components/Uploads/TechnicalDownload"),
    () => import("../Components/Uploads/TechnicalDownload")
  ),
  UpdateDaysHeld: lazyTrio(
    () => import("../Components/Uploads/UpdateDaysHeld"),
    () => import("../Components/Uploads/UpdateDaysHeld"),
    () => import("../Components/Uploads/UpdateDaysHeld")
  ),
  DailyTradesLkFileOldMerge: lazyTrio(
    () => import("../Components/Uploads/DailyTradesLkFileOldMerge"),
    () => import("../Components/Uploads/DailyTradesLkFileOldMerge"),
    () => import("../Components/Uploads/DailyTradesLkFileOldMerge")
  ),
  VolumeBySedol: lazyTrio(
    () => import("../Components/VolumeBySedol/VolumeBySedol"),
    () => import("../Components/VolumeBySedol/VolumeBySedol"),
    () => import("../Components/VolumeBySedol/VolumeBySedol")
  ),
  FactsetVolumeFetch: lazyTrio(
    () => import("../Components/FactsetVolumeFetch/FactsetVolumeFetch"),
    () => import("../Components/FactsetVolumeFetch/FactsetVolumeFetch"),
    () => import("../Components/FactsetVolumeFetch/FactsetVolumeFetch")
  ),
  TearSheetTemplateDashboard: lazyTrio(
    () => import("../Components/TearSheetTemplate/TearSheetTemplateDashboard"),
    () => import("../Components/TearSheetTemplate/TearSheetTemplateDashboard"),
    () => import("../Components/TearSheetTemplate/TearSheetTemplateDashboard")
  ),
};
/* eslint-enable @typescript-eslint/no-explicit-any */

/* ========================================================================
 * Variant picker — given a trio, return the component for the active device.
 * ====================================================================== */
const pick = (
  device: DeviceType,
  trio: { Desktop: any; Mobile: any; Tablet: any }
) => {
  if (device === "mobile") return trio.Mobile;
  if (device === "tablet") return trio.Tablet;
  return trio.Desktop;
};

const RouteFallback = () => (
  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
    <CircularProgress size={40} />
  </Box>
);

// Single-variant admin page — no mobile/tablet split needed.
const UsageAnalyticsDashboard = React.lazy(
  () => import("../Components/UsageAnalytics/UsageAnalyticsDashboard")
);

// Single-variant page — no mobile/tablet split needed.
const OutputSummaryPage = React.lazy(
  () => import("../Components/OutputSummary/OutputSummaryPage")
);
const EmeaApacReportsPage = React.lazy(
  () => import("../Components/EmeaApacReports/EmeaApacReportsPage")
);
const EmeaApacWorkspace = React.lazy(
  () => import("../Components/EmeaApacWorkspace/EmeaApacWorkspace")
);
const EmeaEcmTrackerPage = React.lazy(
  () => import("../Components/EmeaEcmTracker/EmeaEcmTrackerPage")
);
const EmeaEcmTrackerGuard = React.lazy(
  () => import("../Components/EmeaEcmTracker/EmeaEcmTrackerGuard")
);
const UsFoMailsUpload = React.lazy(
  () => import("../Components/Uploads/UsFoMailsUpload")
);
const ConvertsGapUpload = React.lazy(
  () => import("../Components/ConvertsGapReport/ConvertsGapUpload")
);
const ConvertsGapReportMain = React.lazy(
  () => import("../Components/ConvertsGapReport/ConvertsGapReportMain")
);
const UsConvertsEquity = React.lazy(
  () => import("../Components/UsConvertsEquity/UsConvertsEquity")
);
const TechnicalDataRefresh = React.lazy(
  () => import("../Components/TechnicalDataRefresh/TechnicalDataRefresh")
);

const ResponsiveRouters: React.FC = () => {
  const device = useDeviceType();

  /* Eager pages — pick by device synchronously */
  const CapitalMarketsStatic =
    device === "mobile"
      ? CapitalMarketsStaticMobile
      : device === "tablet"
      ? CapitalMarketsStaticTablet
      : CapitalMarketsStaticDesktop;

  const Login =
    device === "mobile" ? LoginMobile : device === "tablet" ? LoginTablet : LoginDesktop;
  const SignUp =
    device === "mobile" ? SignUpMobile : device === "tablet" ? SignUpTablet : SignUpDesktop;
  const ErrorPage =
    device === "mobile" ? ErrorPageMobile : device === "tablet" ? ErrorPageTablet : ErrorPageDesktop;
  const EmailVerification =
    device === "mobile"
      ? EmailVerificationMobile
      : device === "tablet"
      ? EmailVerificationTablet
      : EmailVerificationDesktop;
  const ResetPassword =
    device === "mobile"
      ? ResetPasswordMobile
      : device === "tablet"
      ? ResetPasswordTablet
      : ResetPasswordDesktop;
  const SummaryPopup =
    device === "mobile"
      ? SummaryPopupMobile
      : device === "tablet"
      ? SummaryPopupTablet
      : SummaryPopupDesktop;
  const PageUnderDevelopment =
    device === "mobile"
      ? PageUnderDevelopmentMobile
      : device === "tablet"
      ? PageUnderDevelopmentTablet
      : PageUnderDevelopmentDesktop;
  const MDTechnicalAnalysis =
    device === "mobile"
      ? MDTechnicalAnalysisMobile
      : device === "tablet"
      ? MDTechnicalAnalysisTablet
      : MDTechnicalAnalysisDesktop;
  const ShowQuantAnalysis =
    device === "mobile"
      ? ShowQuantAnalysisMobile
      : device === "tablet"
      ? ShowQuantAnalysisTablet
      : ShowQuantAnalysisDesktop;
  const ShowQuantAnalysisDetails =
    device === "mobile"
      ? ShowQuantAnalysisDetailsMobile
      : device === "tablet"
      ? ShowQuantAnalysisDetailsTablet
      : ShowQuantAnalysisDetailsDesktop;
  const QuantAnalysisUpload =
    device === "mobile"
      ? QuantAnalysisUploadMobile
      : device === "tablet"
      ? QuantAnalysisUploadTablet
      : QuantAnalysisUploadDesktop;

  /* Lazy pages */
  const CapitalMarkets = pick(device, Pages.CapitalMarkets);
  const MonasheeDeals = pick(device, Pages.MonasheeDeals);
  const InvestmentMain = pick(device, Pages.InvestmentMain);
  const TechnicalMain = pick(device, Pages.TechnicalMain);
  const DetailedGapData = pick(device, Pages.DetailedGapData);
  const GroupedGapData = pick(device, Pages.GroupedGapData);
  const RollingGapReport = pick(device, Pages.RollingGapReport);
  const GatorRunHistoryDashboard = pick(device, Pages.GatorRunHistoryDashboard);
  const Logs = pick(device, Pages.Logs);
  const DealStats = pick(device, Pages.DealStats);
  const FOllowOnDiscount = pick(device, Pages.FOllowOnDiscount);
  const MDDScreener = pick(device, Pages.MDDScreener);
  const AllocationCaptureReturn = pick(device, Pages.AllocationCaptureReturn);
  const BankTable = pick(device, Pages.BankTable);
  const FundWiseTable = pick(device, Pages.FundWiseTable);
  const HighYieldsMain = pick(device, Pages.HighYieldsMain);
  const DealStatsMain = pick(device, Pages.DealStatsMain);
  const HYSkewTableMain = pick(device, Pages.HYSkewTableMain);
  const HYTransactionsMain = pick(device, Pages.HYTransactionsMain);
  const MDDSelectedTicker = pick(device, Pages.MDDSelectedTicker);
  const MarketFilters = pick(device, Pages.MarketFilters);
  const ScreenerMain = pick(device, Pages.ScreenerMain);
  const SkewTableMain = pick(device, Pages.SkewTableMain);
  const MacroMain = pick(device, Pages.MacroMain);
  const StockTickerNews = pick(device, Pages.StockTickerNews);
  const ConvertsMain = pick(device, Pages.ConvertsMain);
  const ConvertsDealStatsMain = pick(device, Pages.ConvertsDealStatsMain);
  const ConvertsSkewMain = pick(device, Pages.ConvertsSkewMain);
  const ConvertsTransactionsMain = pick(device, Pages.ConvertsTransactionsMain);
  const MainUpload = pick(device, Pages.MainUpload);
  const FundamentalsTechnical = pick(device, Pages.FundamentalsTechnical);
  const LandingPageMain = pick(device, Pages.LandingPageMain);
  const OperationsDashboard = pick(device, Pages.OperationsDashboard);
  const IncrementalFundamentalUpdate = pick(device, Pages.IncrementalFundamentalUpdate);
  const DailyReportPost = pick(device, Pages.DailyReportPost);
  const ReportWriteUpMain = pick(device, Pages.ReportWriteUpMain);
  const CombinedSelectedTicker = pick(device, Pages.CombinedSelectedTicker);
  const MarketOpportnuityMain = pick(device, Pages.MarketOpportnuityMain);
  const WeeklyMain = pick(device, Pages.WeeklyMain);
  const DeatiledRegionPnlAttribution = pick(device, Pages.DeatiledRegionPnlAttribution);
  const EquityNewDealFormMain = pick(device, Pages.EquityNewDealFormMain);
  const LkFileUpload = pick(device, Pages.LkFileUpload);
  const DetailedDealsView = pick(device, Pages.DetailedDealsView);
  const DetailedLeadBankView = pick(device, Pages.DetailedLeadBankView);
  const PNLTabMain = pick(device, Pages.PNLTabMain);
  const DetailedRegionView = pick(device, Pages.DetailedRegionView);
  const UploadAiInsights = pick(device, Pages.UploadAiInsights);
  const VersionUploadForm = pick(device, Pages.VersionUploadForm);
  const Agents = pick(device, Pages.Agents);
  const AgentOutputView = pick(device, Pages.AgentOutputView);
  const AgentTasksMain = pick(device, Pages.AgentTasksMain);
  const AgentTickerOverview = pick(device, Pages.AgentTickerOverview);
  const BetaTransferMain = pick(device, Pages.BetaTransferMain);
  const DealDetailedGapAnalysis = pick(device, Pages.DealDetailedGapAnalysis);
  const PerplexityChatMain = pick(device, Pages.PerplexityChatMain);
  const HeatMapMain = pick(device, Pages.HeatMapMain);
  const EquityDealsIPOFO = pick(device, Pages.EquityDealsIPOFO);
  const ExportUnifiedDealData = pick(device, Pages.ExportUnifiedDealData);
  const UnifiedDealDataUpload = pick(device, Pages.UnifiedDealDataUpload);
  const TickerDashboard = pick(device, Pages.TickerDashboard);
  const UploadsWriteUpMain = pick(device, Pages.UploadsWriteUpMain);
  const NewDealsCycleMain = pick(device, Pages.NewDealsCycleMain);
  const TickerTrackingWrapper = pick(device, Pages.TickerTrackingWrapper);
  const FOWriteUpMain = pick(device, Pages.FOWriteUpMain);
  const FOFinancialForecastUpload = pick(device, Pages.FOFinancialForecastUpload);
  const DealsTabsLayout = pick(device, Pages.DealsTabsLayout);
  const EquityAiMlPage = pick(device, Pages.EquityAiMlPage);
  const NewDashboardLifeCycleDetails = pick(device, Pages.NewDashboardLifeCycleDetails);
  const WriteUpIPODashbaord = pick(device, Pages.WriteUpIPODashbaord);
  const UploadFactSetTickers = pick(device, Pages.UploadFactSetTickers);
  const FSDealUnifiedMain = pick(device, Pages.FSDealUnifiedMain);
  const DeleteUnifiedDealData = pick(device, Pages.DeleteUnifiedDealData);
  const DailyNoteDeleteTickersData = pick(device, Pages.DailyNoteDeleteTickersData);
  const IPOUploadsPage = pick(device, Pages.IPOUploadsPage);
  const ExcelUploads = pick(device, Pages.ExcelUploads);
  const ABBModelTabs = pick(device, Pages.ABBModelTabs);
  const VALDModelMain = pick(device, Pages.VALDModelMain);
  const ConvertibleBondModelPage = pick(device, Pages.ConvertibleBondModelPage);
  const AIMLResultsHome = pick(device, Pages.AIMLResultsHome);
  const USDashboard = pick(device, Pages.USDashboard);
  const MattermostChat = pick(device, Pages.MattermostChat);
  const MDRMainTableS3Data = pick(device, Pages.MDRMainTableS3Data);
  const SentimentAnalysisTabs = pick(device, Pages.SentimentAnalysisTabs);
  const FewShotAnalysisUpload = pick(device, Pages.FewShotAnalysisUpload);
  const AIFewshotAnalysis = pick(device, Pages.AIFewshotAnalysis);
  const ShowUSSentimentAnalysis = pick(device, Pages.ShowUSSentimentAnalysis);
  const NotesUI = pick(device, Pages.NotesUI);
  const APACEquityAiMlPage = pick(device, Pages.APACEquityAiMlPage);
  const DealMeetingNotesMain = pick(device, Pages.DealMeetingNotesMain);
  const NewDashboardLifeCycleMain = pick(device, Pages.NewDashboardLifeCycleMain);
  const HotDealsMain = pick(device, Pages.HotDealsMain);
  const HotDealsAdminMain = pick(device, Pages.HotDealsAdminMain);
  const FuturePipelineDealsMain = pick(device, Pages.FuturePipelineDealsMain);
  const TickerChange = pick(device, Pages.TickerChange);
  const NewDashboardFOLifeCycleDetails = pick(device, Pages.NewDashboardFOLifeCycleDetails);
  const SignalBoardMain = pick(device, Pages.SignalBoardMain);
  const NewPortfolioRiskUpload = pick(device, Pages.NewPortfolioRiskUpload);
  const UsIndexReturnUpload = pick(device, Pages.UsIndexReturnUpload);
  const RiskDashboard = pick(device, Pages.RiskDashboard);
  const TickerDetail = pick(device, Pages.TickerDetail);
  const RiskTriggers = pick(device, Pages.RiskTriggers);
  const RiskSummary = pick(device, Pages.RiskSummary);
  const RiskAIDocumentUpload = pick(device, Pages.RiskAIDocumentUpload);
  const DocumentUploadTabs = pick(device, Pages.DocumentUploadTabs);
  const AIPortfolioReview = pick(device, Pages.AIPortfolioReview);
  const JayRitterIPOAnalysis = pick(device, Pages.JayRitterIPOAnalysis);
  const SentimentSummary = pick(device, Pages.SentimentSummary);
  const UnsupervisedDealSummary = pick(device, Pages.UnsupervisedDealSummary);
  const PortfolioSummaryTabs = pick(device, Pages.PortfolioSummaryTabs);
  const UploadClaudeSentiment = pick(device, Pages.UploadClaudeSentiment);
  const SummarySignalBoard = pick(device, Pages.SummarySignalBoard);
  const S3DataTransfer = pick(device, Pages.S3DataTransfer);
  const DataDump = pick(device, Pages.DataDump);
  const JRitterAgentMain = pick(device, Pages.JRitterAgentMain);
  const JUploadPage = pick(device, Pages.JUploadPage);
  const PortfolioAgentMain = pick(device, Pages.PortfolioAgentMain);
  const GatorPostIpoAgentMain = pick(device, Pages.GatorPostIpoAgentMain);
  const GatorPostIpoUpload = pick(device, Pages.GatorPostIpoUpload);
  const IpoGatorRunDashboard = pick(device, Pages.IpoGatorRunDashboard);
  const DatabaseExplorer = pick(device, Pages.DatabaseExplorer);
  const CIOPortfolioReviewWizard = pick(device, Pages.CIOPortfolioReviewWizard);
  const DealsDataDashboard = pick(device, Pages.DealsDataDashboard);
  const OpenClawChatPage = pick(device, Pages.OpenClawChatPage);
  const OpenClawBudgetAdminPage = pick(device, Pages.OpenClawBudgetAdminPage);
  const MonthlyDecDashboard = pick(device, Pages.MonthlyDecDashboard);
  const MonthlyTRSheetDashboard = pick(device, Pages.MonthlyTRSheetDashboard);
  const FundMonthlyDataDashboard = pick(device, Pages.FundMonthlyDataDashboard);
  const MonthlyFundReportsDashboard = pick(device, Pages.MonthlyFundReportsDashboard);
  const AdamMonthlyReportDashboard = pick(device, Pages.AdamMonthlyReportDashboard);
  const TechnicalDownload = pick(device, Pages.TechnicalDownload);
  const UpdateDaysHeld = pick(device, Pages.UpdateDaysHeld);
  const DailyTradesLkFileOldMerge = pick(device, Pages.DailyTradesLkFileOldMerge);
  const AumUpdate = pick(device, Pages.AumUpdate);
  const PortfolioAumUpdate = pick(device, Pages.PortfolioAumUpdate);
  const ModelPortfolioPositionUpload = pick(device, Pages.ModelPortfolioPositionUpload);
  const VolumeBySedol = pick(device, Pages.VolumeBySedol);
  const FactsetVolumeFetch = pick(device, Pages.FactsetVolumeFetch);
  const TearSheetTemplateDashboard = pick(device, Pages.TearSheetTemplateDashboard);

  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<CapitalMarketsStatic />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/summarypopup" element={<SummaryPopup />} />
          <Route path="/user_log" element={<Logs />} />
          <Route path="/usage_analytics" element={<AuthGuard><UsageAnalyticsDashboard /></AuthGuard>} />
          <Route path="/output" element={<AuthGuard><OutputSummaryPage /></AuthGuard>} />
          <Route path="/emea-apac/reports" element={<AuthGuard><EmeaApacReportsPage /></AuthGuard>} />
          <Route path="/emea-apac/emea/ecm-tracker" element={<AuthGuard><EmeaEcmTrackerGuard><EmeaEcmTrackerPage /></EmeaEcmTrackerGuard></AuthGuard>} />
          <Route path="/emea-apac/:region" element={<AuthGuard><EmeaApacWorkspace /></AuthGuard>} />
          <Route path="/us_fo_mails/upload/" element={<AuthGuard><UsFoMailsUpload /></AuthGuard>} />
          <Route path="/us_fo_mails/analyze/" element={<AuthGuard><UsFoMailsUpload mode="analysis" /></AuthGuard>} />

          <Route path="/equity/ai_ml_models" element={<AuthGuard><EquityAiMlPage /></AuthGuard>} />
          <Route path="/equity/apac_ai_ml_models" element={<AuthGuard><APACEquityAiMlPage /></AuthGuard>} />
          <Route path="/equity/ai_ml_results" element={<AuthGuard><AIMLResultsHome /></AuthGuard>} />

          <Route path="/opportunity/equity" element={<AuthGuard><MarketOpportnuityMain /></AuthGuard>}>
            <Route path="search" element={<CombinedSelectedTicker ticker={"AS"} />} />
            <Route path="deal-stats" element={<MarketFilters />} />
            <Route path="skew-table" element={<SkewTableMain />} />
            <Route path="deal-filter" element={<ScreenerMain />} />
            <Route path="mdd_deal_stats" element={<DealStats />} />
            <Route path="weekly-tracking" element={<WeeklyMain />} />
            <Route path="gap-analysis" element={<AllocationCaptureReturn />} />
            <Route path="gap-report" element={<RollingGapReport />} />
          </Route>

          <Route path="/opportunity/high-yield" element={<AuthGuard><HighYieldsMain /></AuthGuard>}>
            <Route path="search" element={<HighYieldsMain />} />
            <Route path="deal-stats" element={<DealStatsMain />} />
            <Route path="skew-table" element={<HYSkewTableMain />} />
            <Route path="transactions" element={<HYTransactionsMain />} />
          </Route>

          <Route path="/opportunity/converts" element={<AuthGuard><ConvertsMain /></AuthGuard>}>
            <Route path="search" element={<ConvertsMain />} />
            <Route path="deal-stats" element={<ConvertsDealStatsMain />} />
            <Route path="skew-table" element={<ConvertsSkewMain />} />
            <Route path="transactions" element={<ConvertsTransactionsMain />} />
          </Route>

          <Route path="/opportunity/converts/:ticker" element={<AuthGuard><ConvertsMain /></AuthGuard>} />
          <Route path="/opportunity/high-yield/:ticker" element={<AuthGuard><HighYieldsMain /></AuthGuard>} />
          <Route path="/equity/monashee-deals" element={<AuthGuard><MonasheeDeals /></AuthGuard>}>
            <Route path="search" element={<MDDSelectedTicker ticker={"CGRX"} />} />
            <Route path="mdd_deal_stats" element={<DealStats />} />
            <Route path="gap-analysis" element={<AllocationCaptureReturn />} />
            <Route path="follow-on-discount" element={<FOllowOnDiscount />} />
            <Route path="weekly-tracking" element={<WeeklyMain />} />
            <Route path="by-bank" element={<BankTable selectedFilters={{}} />} />
            <Route path="screener" element={<MDDScreener />} />
          </Route>

          <Route path="/portfolio-attribution" element={<Navigate to="/portfolio-attribution/summary_pnl" />} />
          <Route path="/portfolio-attribution/:tab" element={<AuthGuard><PNLTabMain /></AuthGuard>} />
          <Route path="/portfolio-attribution/details/:assetType" element={<AuthGuard><DeatiledRegionPnlAttribution /></AuthGuard>} />

          <Route path="/macro/prime" element={<AuthGuard><InvestmentMain /></AuthGuard>} />
          <Route path="/equity/technical/:ticker" element={<AuthGuard><TechnicalMain /></AuthGuard>} />
          <Route path="/opportunity/equity/:ticker" element={<AuthGuard><MarketOpportnuityMain /></AuthGuard>} />
          <Route path="/equity/tickerperformance/:ticker" element={<AuthGuard><CapitalMarkets /></AuthGuard>} />
          <Route path="/equity/portfolio-attribution/fund/:fund" element={<AuthGuard><FundWiseTable /></AuthGuard>} />
          <Route path="/equity/detailed_gap_analysis" element={<AuthGuard><DetailedGapData /></AuthGuard>} />
          <Route path="/equity/grouped_gap_analysis" element={<AuthGuard><GroupedGapData /></AuthGuard>} />
          <Route path="/gator_run_history" element={<AuthGuard><GatorRunHistoryDashboard /></AuthGuard>} />

          <Route path="/highyield/dealperformance/:deal" element={<AuthGuard><HighYieldsMain /></AuthGuard>} />
          <Route path="/converts/dealperformance/:deal" element={<AuthGuard><ConvertsMain /></AuthGuard>} />

          <Route path="/deals/new_deal_form" element={<AuthGuard><EquityNewDealFormMain /></AuthGuard>} />
          <Route path="/deals/deal_Tracking" element={<AuthGuard><TickerDashboard /></AuthGuard>} />

          <Route path="/macro/sector" element={<AuthGuard><MacroMain /></AuthGuard>} />
          <Route path="/macro/news-summary" element={<AuthGuard><StockTickerNews /></AuthGuard>} />

          <Route path="/uploads" element={<AuthGuard><MainUpload /></AuthGuard>} />
          <Route path="/ipouploads" element={<AuthGuard><IPOUploadsPage /></AuthGuard>} />
          <Route path="/lk_upload" element={<AuthGuard><LkFileUpload /></AuthGuard>} />
          <Route path="/converts_gap_upload" element={<AuthGuard><ConvertsGapUpload /></AuthGuard>} />
          <Route path="/converts_gap_report" element={<AuthGuard><ConvertsGapReportMain /></AuthGuard>} />
          <Route path="/us-converts-equity" element={<AuthGuard><UsConvertsEquity /></AuthGuard>} />
          <Route path="/technical_data_refresh" element={<AuthGuard><TechnicalDataRefresh /></AuthGuard>} />
          <Route path="/fs_upload" element={<AuthGuard><UploadFactSetTickers /></AuthGuard>} />
          <Route path="/data_upload" element={<AuthGuard><FundamentalsTechnical /></AuthGuard>} />
          <Route path="/upcoming" element={<AuthGuard><ExcelUploads /></AuthGuard>} />
          <Route path="/ai_upload" element={<AuthGuard><UploadAiInsights /></AuthGuard>} />

          <Route element={<AuthGuard><DealsTabsLayout /></AuthGuard>}>
            <Route path="/new_deal_data_upload" element={<Navigate to="/download_deals_data" replace />} />
            <Route path="/download_deals_data" element={<ExportUnifiedDealData />} />
            <Route path="/delete_new_deal_data" element={<DeleteUnifiedDealData />} />
            <Route path="/deal_data_upload" element={<UnifiedDealDataUpload />} />
          </Route>

          <Route path="/equity/ipo_dashboard" element={<AuthGuard><WriteUpIPODashbaord /></AuthGuard>} />
          <Route path="/equity/ipo_dashboard/:ticker" element={<AuthGuard><WriteUpIPODashbaord /></AuthGuard>} />

          <Route path="/error" element={<ErrorPage />} />
          <Route path="/email-verification" element={<EmailVerification />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/machine_learning/converts" element={<PageUnderDevelopment />} />
          <Route path="/converts/portfolio-attribution" element={<PageUnderDevelopment />} />
          <Route path="/machine_learning/high-yield" element={<PageUnderDevelopment />} />
          <Route path="/highyield/portfolio-attribution" element={<PageUnderDevelopment />} />

          <Route path="/od" element={<AuthGuard><OperationsDashboard /></AuthGuard>} />
          <Route path="/incremental_fundamental_update" element={<AuthGuard><IncrementalFundamentalUpdate /></AuthGuard>} />
          <Route path="/opportunity/summary" element={<AuthGuard><LandingPageMain /></AuthGuard>} />
          <Route path="/opportunity/equity/passed_deals" element={<AuthGuard><EquityDealsIPOFO /></AuthGuard>} />
          <Route path="/passed_deals" element={<Navigate to="/opportunity/equity/passed_deals" replace />} />
          <Route path="/opportunity/pastdeals" element={<Navigate to="/opportunity/equity/passed_deals" replace />} />
          <Route path="/opportunity/monashee_daily_report" element={<AuthGuard><MDRMainTableS3Data /></AuthGuard>} />

          <Route path="/deals/dashboard" element={<AuthGuard><NewDealsCycleMain /></AuthGuard>} />
          <Route path="/deals/dashboard/Tracking" element={<AuthGuard><TickerTrackingWrapper /></AuthGuard>} />
          <Route path="/deals/new_dashboard/details" element={<AuthGuard><NewDashboardLifeCycleDetails /></AuthGuard>} />
          <Route path="/deals/new_dashboard/fo_details" element={<AuthGuard><NewDashboardFOLifeCycleDetails /></AuthGuard>} />

          <Route path="/reportdata" element={<AuthGuard><DailyReportPost /></AuthGuard>} />
          <Route path="/data-analytics/writeups" element={<AuthGuard><ReportWriteUpMain /></AuthGuard>} />

          <Route path="/gen_ai_tool" element={<AuthGuard><PerplexityChatMain /></AuthGuard>} />
          <Route path="/gapreport" element={<AuthGuard><DealDetailedGapAnalysis /></AuthGuard>} />
          <Route path="/genai_data_set" element={<AuthGuard><HeatMapMain /></AuthGuard>} />

          <Route path="/detailed-deals" element={<AuthGuard><DetailedDealsView /></AuthGuard>} />
          <Route path="/detailed-banks" element={<AuthGuard><DetailedLeadBankView /></AuthGuard>} />
          <Route path="/detailed-region" element={<AuthGuard><DetailedRegionView /></AuthGuard>} />
          <Route path="/version" element={<AuthGuard><VersionUploadForm /></AuthGuard>} />

          <Route path="/pdf_upload" element={<AuthGuard><UploadsWriteUpMain /></AuthGuard>} />

          <Route path="/equity/fo_dashboard" element={<AuthGuard><FOWriteUpMain /></AuthGuard>} />
          <Route path="/equity/fo_dashboard/:ticker" element={<AuthGuard><FOWriteUpMain /></AuthGuard>} />
          <Route path="/fo_financial_forecasts_upload" element={<AuthGuard><FOFinancialForecastUpload /></AuthGuard>} />

          <Route path="/fs_new_deal_data" element={<AuthGuard><FSDealUnifiedMain /></AuthGuard>} />
          <Route path="/daily_note" element={<AuthGuard><DailyNoteDeleteTickersData /></AuthGuard>} />
          <Route path="/equity/abb_model" element={<AuthGuard><ABBModelTabs /></AuthGuard>} />
          <Route path="/convertable-bond-model" element={<AuthGuard><ConvertibleBondModelPage /></AuthGuard>} />
          <Route path="/equity/vald_model" element={<AuthGuard><VALDModelMain /></AuthGuard>} />

          <Route path="/chat" element={<AuthGuard><MattermostChat /></AuthGuard>} />
          <Route path="/notesui" element={<AuthGuard><NotesUI /></AuthGuard>} />

          <Route path="/sentiment_analysis" element={<AuthGuard><SentimentAnalysisTabs /></AuthGuard>} />
          <Route path="/fewshot_analysis_upload" element={<AuthGuard><FewShotAnalysisUpload /></AuthGuard>} />
          <Route path="/ai_fewshot_analysis" element={<AuthGuard><AIFewshotAnalysis /></AuthGuard>} />
          <Route path="/ai_sentiment_summary" element={<AuthGuard><SentimentSummary /></AuthGuard>} />
          <Route path="/ai_sentiment_view" element={<AuthGuard><ShowUSSentimentAnalysis /></AuthGuard>} />
          <Route path="/ai_unsupervised_summary" element={<AuthGuard><UnsupervisedDealSummary /></AuthGuard>} />
          <Route path="/portfolio_summary" element={<AuthGuard><PortfolioSummaryTabs /></AuthGuard>} />
          <Route path="/upload_claude_sentiment" element={<AuthGuard><UploadClaudeSentiment /></AuthGuard>} />
          <Route path="/summary_signal_board" element={<AuthGuard><SummarySignalBoard /></AuthGuard>} />
          <Route path="/quant_agent" element={<AuthGuard><ShowQuantAnalysis /></AuthGuard>} />
          <Route path="/quant-analysis/ipo-dashboard/:ticker" element={<AuthGuard><QuantIPODashboardPage /></AuthGuard>} />
          <Route path="/quant-analysis/technical-details/:ticker" element={<AuthGuard><QuantAnalysisDetailsModal /></AuthGuard>} />
          <Route path="/quant-analysis/:ticker" element={<AuthGuard><ShowQuantAnalysisDetails /></AuthGuard>} />
          <Route path="/quant_analysis_upload" element={<AuthGuard><QuantAnalysisUpload /></AuthGuard>} />

          <Route path="/deal_meeting_notes" element={<AuthGuard><DealMeetingNotesMain /></AuthGuard>} />
          <Route path="/deals/new_dashboard" element={<AuthGuard><NewDashboardLifeCycleMain /></AuthGuard>} />
          <Route path="/us-dashboard" element={<AuthGuard><USDashboard /></AuthGuard>} />
          <Route path="/deals/hot_deals" element={<AuthGuard><HotDealsMain /></AuthGuard>} />
          <Route path="/hot_deals_admin" element={<AuthGuard><HotDealsAdminMain /></AuthGuard>} />
          <Route path="page_under_development" element={<AuthGuard><PageUnderDevelopment /></AuthGuard>} />

          <Route path="/deals/future_pipeline" element={<AuthGuard><FuturePipelineDealsMain /></AuthGuard>} />
          <Route path="/deals_data" element={<AuthGuard><DealsDataDashboard /></AuthGuard>} />
          <Route path="/ticker_change" element={<AuthGuard><TickerChange /></AuthGuard>} />

          <Route path="/risk_pnl" element={<AuthGuard><RiskDashboard /></AuthGuard>} />
          <Route path="/risk_pnl/ticker-detail" element={<AuthGuard><TickerDetail /></AuthGuard>} />
          {/* Keep the existing URL and expose /risk_pnl as an additional entry point. */}
          <Route path="/risk_report_pnl_report" element={<AuthGuard><RiskDashboard /></AuthGuard>} />
          <Route path="/risk_report_pnl_report/ticker-detail" element={<AuthGuard><TickerDetail /></AuthGuard>} />
          <Route path="/risk_triggers" element={<AuthGuard><RiskTriggers /></AuthGuard>} />
          <Route path="/risk_summary" element={<AuthGuard><RiskSummary /></AuthGuard>} />
          <Route path="/portfolio-intelligence/:agent?" element={<AuthGuard><PortfolioIntelligence /></AuthGuard>} />
          <Route path="/risk_upload" element={<AuthGuard><NewPortfolioRiskUpload /></AuthGuard>} />
          <Route path="/us_index_return_upload" element={<AuthGuard><UsIndexReturnUpload /></AuthGuard>} />

          <Route path="/risk_document_upload" element={<AuthGuard><RiskAIDocumentUpload /></AuthGuard>} />
          <Route path="/portfolio_document_upload" element={<AuthGuard><DocumentUploadTabs /></AuthGuard>} />

          <Route path="/ai_portfolio_review" element={<AuthGuard><AIPortfolioReview mode="portfolioReview" reviewTab="portfolio" /></AuthGuard>} />
          <Route path="/ai_risk_review" element={<AuthGuard><AIPortfolioReview mode="portfolioReview" reviewTab="risk" /></AuthGuard>} />
          <Route path="/last_30_days_ai_ranking" element={<AuthGuard><AIPortfolioReview mode="stockRanking" /></AuthGuard>} />
          <Route path="/gator_ipo_analysis" element={<AuthGuard><JayRitterIPOAnalysis /></AuthGuard>} />

          <Route path="/agents/dashboard" element={<AuthGuard><Agents /></AuthGuard>} />
          <Route path="/agents_tasks" element={<AuthGuard><AgentTasksMain /></AuthGuard>} />
          <Route path="/agents/ticker/:ticker" element={<AuthGuard><AgentTickerOverview /></AuthGuard>} />
          <Route path="/beta_transfer" element={<AuthGuard><BetaTransferMain /></AuthGuard>} />
          <Route path="/agents/:agentId/output" element={<AuthGuard><AgentOutputView /></AuthGuard>} />
          <Route path="/agents/:agentId/output/:outputId" element={<AuthGuard><AgentOutputView /></AuthGuard>} />
          <Route path="/signals/board" element={<AuthGuard><SignalBoardMain /></AuthGuard>} />
          <Route path="/s3" element={<AuthGuard><S3DataTransfer /></AuthGuard>} />
          <Route path="/data_dump" element={<AuthGuard><DataDump /></AuthGuard>} />
          <Route path="/jritter_agent" element={<AuthGuard><JRitterAgentMain /></AuthGuard>} />
          <Route path="/j_upload" element={<AuthGuard><JUploadPage /></AuthGuard>} />
          <Route path="/portfolio_agent" element={<AuthGuard><PortfolioAgentMain /></AuthGuard>} />
          <Route path="/gator_post_ipo" element={<AuthGuard><GatorPostIpoAgentMain /></AuthGuard>} />
          <Route path="/gator_post_ipo/upload" element={<AuthGuard><GatorPostIpoUpload /></AuthGuard>} />
          <Route path="/ipo_gator_run" element={<AuthGuard><IpoGatorRunDashboard /></AuthGuard>} />
          <Route path="/database_explorer" element={<AuthGuard><DatabaseExplorer /></AuthGuard>} />
          <Route path="/cio_portfolio_review" element={<AuthGuard><CIOPortfolioReviewWizard /></AuthGuard>} />

          <Route path="/md_technical_analysis" element={<AuthGuard><MDTechnicalAnalysis /></AuthGuard>} />
          <Route path="/openclaw_chat" element={<AuthGuard><OpenClawChatPage /></AuthGuard>} />
          <Route path="/openclaw_budget_admin" element={<AuthGuard><OpenClawBudgetAdminPage /></AuthGuard>} />
          <Route path="/monthly_dec" element={<AuthGuard><MonthlyDecDashboard /></AuthGuard>} />
          <Route path="/monthly_tr_sheet" element={<AuthGuard><MonthlyTRSheetDashboard /></AuthGuard>} />
          <Route path="/fund_monthly_data" element={<AuthGuard><FundMonthlyDataDashboard /></AuthGuard>} />
          <Route path="/monthly_fund_reports" element={<AuthGuard><MonthlyFundReportsDashboard /></AuthGuard>} />
          <Route path="/adam_monthly_report" element={<AuthGuard><AdamMonthlyReportDashboard /></AuthGuard>} />
          <Route path="/tech_download" element={<AuthGuard><TechnicalDownload /></AuthGuard>} />
          <Route path="/days_held_update" element={<AuthGuard><UpdateDaysHeld /></AuthGuard>} />
          <Route path="/daily_trades_lk_file_merge_old" element={<AuthGuard><DailyTradesLkFileOldMerge /></AuthGuard>} />
          <Route path="/hot_deals_portfolio_aum_update" element={<AuthGuard><AumUpdate /></AuthGuard>} />
          <Route path="/portfolio_position_aum_update" element={<AuthGuard><PortfolioAumUpdate /></AuthGuard>} />
          <Route path="/model_portfolio_position_upload" element={<AuthGuard><ModelPortfolioPositionUpload /></AuthGuard>} />
          <Route path="/volume_by_sedol" element={<AuthGuard><VolumeBySedol /></AuthGuard>} />
          <Route path="/factset_volume_fetch" element={<AuthGuard><FactsetVolumeFetch /></AuthGuard>} />
          <Route path="/tear_sheet_template" element={<AuthGuard><TearSheetTemplateDashboard /></AuthGuard>} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

export default ResponsiveRouters;
